import type { RepeatMode } from '@/types/memorization';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';

/**
 * Audio playback UI for the current ayah.
 *
 * Receives state + handlers from `useAyahAudio` (in the parent
 * MemorizationView) via props — this component is purely presentational.
 *
 * Layout: play/pause/stop controls on one row, repeat-mode segmented
 * selector below, loading/error pills float into view as needed.
 *
 * Touch-friendly: all buttons are ≥44px tall on mobile.
 */

type AudioControlsProps = {
  isPlaying: boolean;
  isLoading: boolean;
  error: string | null;
  repeatMode: RepeatMode;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onRetry: () => void;
  onRepeatModeChange: (mode: RepeatMode) => void;
  /** When true, render the repeat options more prominently (Repeat mode). */
  prominent?: boolean;
};

const REPEAT_OPTIONS: { value: RepeatMode; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'three', label: '3×' },
  { value: 'five', label: '5×' },
  { value: 'loop', label: 'Loop' },
];

export function AudioControls({
  isPlaying,
  isLoading,
  error,
  repeatMode,
  onPlay,
  onPause,
  onStop,
  onRetry,
  onRepeatModeChange,
  prominent = false,
}: AudioControlsProps) {
  const handlePlayPause = () => {
    if (isPlaying) onPause();
    else onPlay();
  };

  return (
    <div
      className={cn(
        'p-5 rounded-2xl',
        'border border-ink-200/60 dark:border-ink-800/60',
        prominent
          ? 'bg-accent/5 dark:bg-accent/10 border-accent/30'
          : 'bg-ink-50 dark:bg-ink-900/60',
      )}
      role="region"
      aria-label="Audio playback"
    >
      {error && (
        <div
          role="alert"
          className="mb-3 flex items-center justify-between px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="ml-3 underline underline-offset-2 text-xs font-medium"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Button
          variant={isPlaying ? 'secondary' : 'primary'}
          size={prominent ? 'lg' : 'md'}
          onClick={handlePlayPause}
          disabled={isLoading && !isPlaying}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          className="flex-1 sm:flex-none sm:min-w-32"
        >
          {isLoading && !isPlaying ? 'Loading…' : isPlaying ? '❚❚ Pause' : '▶ Play'}
        </Button>
        <Button
          variant="ghost"
          size={prominent ? 'lg' : 'md'}
          onClick={onStop}
          disabled={!isPlaying && !isLoading}
          aria-label="Stop and reset"
        >
          ■ Stop
        </Button>
      </div>

      <div
        role="radiogroup"
        aria-label="Repeat mode"
        className="flex flex-wrap items-center gap-2"
      >
        <span className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400 mr-1">
          Repeat
        </span>
        {REPEAT_OPTIONS.map((opt) => {
          const active = opt.value === repeatMode;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onRepeatModeChange(opt.value)}
              className={cn(
                'inline-flex items-center justify-center',
                'h-9 min-w-14 px-3 rounded-full text-sm font-medium',
                'border transition-colors',
                active
                  ? 'bg-accent text-ink-50 border-accent shadow-[inset_0_-1px_0_rgba(0,0,0,0.12)]'
                  : 'bg-white dark:bg-ink-900 border-ink-200 dark:border-ink-800 text-ink-700 dark:text-ink-200 hover:bg-ink-100 dark:hover:bg-ink-800',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
